
import React, { useState, useEffect, useCallback } from "react";
import { SocialPoll, UserProgram } from "@/api/entities";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import Icon from "@/components/ui/Icon";
import { CalendarRange } from "lucide-react";
import PollCard from "../social/PollCard";

export default function WhosInModal({ camp, isOpen, onClose, currentUser }) {
  const [existingPolls, setExistingPolls] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [customQuestion, setCustomQuestion] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingPoll, setIsCreatingPoll] = useState(false);

  const loadExistingPolls = useCallback(async () => {
    if (!camp) return;
    
    setIsLoading(true);
    try {
      // Look for polls with matching camp_id (could be camp.id or from UserProgram)
      const polls = await SocialPoll.filter({ 
        camp_id: camp.id,
        status: "active" 
      });
      setExistingPolls(polls);
    } catch (error) {
      console.error("Error loading existing polls:", error);
    } finally {
      setIsLoading(false);
    }
  }, [camp]); // Dependency: camp ensures `camp.id` is stable or `loadExistingPolls` recreates when camp changes

  useEffect(() => {
    if (isOpen && camp) {
      loadExistingPolls();
      setCustomQuestion(`Who's interested in ${camp.name}?`);
    }
  }, [isOpen, camp, loadExistingPolls]); // Added loadExistingPolls to dependencies

  const handleVote = async (pollId, vote) => {
    if (!currentUser) return;

    try {
      const poll = existingPolls.find(p => p.id === pollId);
      if (!poll) return;

      // Remove existing vote from this user
      const filteredVotes = poll.votes?.filter(v => v.user_email !== currentUser.email) || [];
      
      // Add new vote
      const newVotes = [
        ...filteredVotes,
        {
          user_email: currentUser.email,
          user_name: currentUser.full_name || "Anonymous",
          vote: vote,
          child_names: currentUser.children?.map(c => c.name) || ["Child"]
        }
      ];

      await SocialPoll.update(pollId, { votes: newVotes });
      
      // Update local state
      setExistingPolls(prev => prev.map(p => 
        p.id === pollId 
          ? { ...p, votes: newVotes }
          : p
      ));
    } catch (error) {
      console.error("Error voting on poll:", error);
    }
  };

  const getUserVote = (poll) => {
    if (!currentUser) return null;
    const userVote = poll.votes?.find(v => v.user_email === currentUser.email);
    return userVote?.vote || null;
  };

  const handleCreatePoll = async () => {
    if (!customQuestion.trim() || !camp || !currentUser) return;

    setIsCreatingPoll(true);
    try {
      // First, save this camp to user's programs if not already saved
      const existingPrograms = await UserProgram.filter({
        user_email: currentUser.email,
        "program_data.id": camp.id
      });

      if (existingPrograms.length === 0) {
        await UserProgram.create({
          user_email: currentUser.email,
          program_type: "camp",
          program_data: {
            id: camp.id,
            name: camp.name,
            general_location: (camp.location && typeof camp.location === 'object') 
              ? (camp.general_location || camp.formatted_address || camp.address || null)
              : (camp.location || camp.general_location || camp.formatted_address || camp.address || null),
            schedule_dates: camp.start_date && camp.end_date ? 
              `${new Date(camp.start_date).toLocaleDateString()} to ${new Date(camp.end_date).toLocaleDateString()}` : null,
            cost_structure: camp.price ? `$${camp.price}` : null,
            image_url: camp.image_url,
            latitude: camp.latitude,
            longitude: camp.longitude,
            category: camp.category,
            price: camp.price,
            start_date: camp.start_date,
            end_date: camp.end_date,
            age_min: camp.age_min,
            age_max: camp.age_max,
            rating: camp.rating
          },
          status: "favorite",
          year_tracked: new Date().getFullYear(),
          child_names: currentUser.children?.map(c => c.name) || []
        });
      }

      // Create the poll
      await SocialPoll.create({
        camp_id: camp.id,
        question: customQuestion.trim(),
        status: "active",
        votes: []
      });

      // Reload polls
      await loadExistingPolls();
      setShowCreateForm(false);
      
    } catch (error) {
      console.error("Error creating poll:", error);
      alert("Failed to create poll. Please try again.");
    } finally {
      setIsCreatingPoll(false);
    }
  };

  const handleClose = () => {
    setShowCreateForm(false);
    setExistingPolls([]);
    onClose();
  };

  if (!isOpen || !camp) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
      <Card className="w-full max-w-2xl mx-auto bg-white shadow-2xl rounded-2xl flex flex-col max-h-[90vh]">
        <CardHeader className="flex flex-row items-center justify-between border-b sticky top-0 bg-white z-10 px-6 py-4">
          <CardTitle className="flex items-center gap-2">
            <Icon name="users" size="md" color="primary" />
            Who's In for {camp.name}?
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <Icon name="x" size="sm" />
          </Button>
        </CardHeader>
        
        <CardContent className="flex-1 overflow-y-auto px-6 py-5">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <Icon name="loader" size="lg" color="primary" className="animate-spin" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Camp Info */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="flex gap-3">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-100 to-teal-100 flex items-center justify-center text-2xl flex-shrink-0">
                    {camp.category === 'sports' ? '⚽' :
                     camp.category === 'arts' ? '🎨' : 
                     camp.category === 'stem' ? '🔬' : '🏕️'}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-slate-900 mb-1">{camp.name}</h4>
                    <p className="text-sm text-slate-600 mb-2">
                      {(() => {
                        // Handle location as object (with lat/lng) or string
                        if (camp.location && typeof camp.location === 'object') {
                          return camp.general_location || camp.formatted_address || camp.address || 'Location not available';
                        }
                        return camp.location || camp.general_location || camp.formatted_address || camp.address || 'Location not available';
                      })()}
                    </p>
                    <div className="flex gap-2">
                      <Badge className="bg-purple-100 text-brand-700 border-0 text-xs">
                        Ages {camp.age_min}-{camp.age_max}
                      </Badge>
                      <Badge className="bg-teal-100 text-teal-700 border-0 text-xs">
                        ${camp.price}/week
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              {/* Existing Polls */}
              {existingPolls.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Icon name="message" size="sm" />
                    Active Polls ({existingPolls.length})
                  </h4>
                  <div className="space-y-3">
                    {existingPolls.map((poll) => (
                      <PollCard
                        key={poll.id}
                        poll={poll}
                        camp={camp}
                        onVote={handleVote}
                        currentUserVote={getUserVote(poll)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Create New Poll Section */}
              {!showCreateForm ? (
                <div className="text-center">
                  {existingPolls.length === 0 && (
                    <div className="mb-4">
                      <Icon name="message" size="2xl" color="muted" className="mx-auto mb-3" />
                      <h4 className="font-medium text-slate-900 mb-2">No polls yet</h4>
                      <p className="text-slate-600 text-sm mb-4">
                        Be the first to gauge interest for this camp!
                      </p>
                    </div>
                  )}
                  <Button
                    onClick={() => setShowCreateForm(true)}
                    className="bg-brand-600 hover:bg-brand-700"
                  >
                    <Icon name="plus" size="sm" className="mr-2" />
                    Start a New Poll
                  </Button>
                </div>
              ) : (
                <div className="border-t pt-6">
                  <h4 className="font-semibold text-slate-900 mb-3">Create New Poll</h4>
                  <div className="space-y-4">
                    <Textarea
                      value={customQuestion}
                      onChange={(e) => setCustomQuestion(e.target.value)}
                      placeholder="Who's interested in this camp?"
                      className="h-20"
                    />
                    <p className="text-xs text-slate-500">
                      Your friends will be able to vote "Yes", "Maybe", or "No"
                    </p>
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        onClick={() => setShowCreateForm(false)}
                        disabled={isCreatingPoll}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleCreatePoll}
                        disabled={!customQuestion.trim() || isCreatingPoll}
                        className="flex-1 bg-brand-600 hover:bg-brand-700"
                      >
                        {isCreatingPoll ? (
                          <Icon name="loader" size="sm" className="mr-2 animate-spin" />
                        ) : (
                          <CalendarRange size={14} className="mr-2" />
                        )}
                        Create Poll
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
